/**
 * Queue Job Viewer Modal
 * Exchange-style queue management interface with filtering, sorting, pagination,
 * job details panel, and bulk actions.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  XMarkIcon,
  ArrowPathIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  FunnelIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  PlayIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon,
  EnvelopeIcon,
  ServerIcon,
} from '@heroicons/react/24/outline';
import EdgeMailAdminAPI from '../../services/EdgeMailAdminAPI';
import type {
  QueueJobDetails,
  QueueJobStatus,
  QueueType,
  QueueJobListOptions,
} from '../../types/admin.types';

// ============================================================================
// TYPES
// ============================================================================

interface QueueJobViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialQueueType?: QueueType;
  initialStatus?: QueueJobStatus;
}

type SortField = 'queuedAt' | 'fromAddress' | 'toAddress' | 'subject' | 'sizeBytes' | 'attemptsMade';

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_TABS: { key: QueueJobStatus; label: string; icon: React.ElementType; color: string }[] = [
  { key: 'waiting', label: 'Waiting', icon: ClockIcon, color: 'text-lex-accent' },
  { key: 'active', label: 'Active', icon: PlayIcon, color: 'text-green-600 dark:text-green-400' },
  { key: 'failed', label: 'Failed', icon: ExclamationTriangleIcon, color: 'text-red-600 dark:text-red-400' },
  { key: 'delayed', label: 'Delayed', icon: ClockIcon, color: 'text-amber-600 dark:text-amber-400' },
  { key: 'dlq', label: 'Dead Letter', icon: TrashIcon, color: 'text-red-600 dark:text-red-400' },
  { key: 'completed', label: 'Completed', icon: CheckCircleIcon, color: 'text-gray-500 dark:text-gray-400' },
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatFullDate(dateStr: string | undefined): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function getStatusBadge(status: QueueJobStatus): { bg: string; text: string; label: string } {
  switch (status) {
    case 'waiting':
      return { bg: 'bg-lex-accent-soft', text: 'text-lex-accent', label: 'Waiting' };
    case 'active':
      return { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', label: 'Active' };
    case 'failed':
      return { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'Failed' };
    case 'delayed':
      return { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', label: 'Delayed' };
    case 'dlq':
      return { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', label: 'DLQ' };
    case 'completed':
      return { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', label: 'Completed' };
    default:
      return { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', label: status };
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const QueueJobViewerModal: React.FC<QueueJobViewerModalProps> = ({
  isOpen,
  onClose,
  initialQueueType = 'inbound',
  initialStatus = 'failed',
}) => {
  const queryClient = useQueryClient();

  // State
  const [queueType, setQueueType] = useState<QueueType>(initialQueueType);
  const [status, setStatus] = useState<QueueJobStatus>(initialStatus);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(15);
  const [sortBy, setSortBy] = useState<SortField>('queuedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [selectedJobDetail, setSelectedJobDetail] = useState<QueueJobDetails | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    domain: '',
    from: '',
    to: '',
    subject: '',
  });

  // Query options
  const queryOptions: Partial<QueueJobListOptions> = useMemo(() => ({
    status,
    queueType,
    page,
    pageSize,
    sortBy,
    sortOrder,
    filterDomain: filters.domain || undefined,
    filterFrom: filters.from || undefined,
    filterTo: filters.to || undefined,
    filterSubject: filters.subject || undefined,
  }), [status, queueType, page, pageSize, sortBy, sortOrder, filters]);

  // Query key
  const queryKey = ['queueJobs', queryOptions];

  // Fetch jobs
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => EdgeMailAdminAPI.getQueueJobs(queryOptions),
    enabled: isOpen,
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Mutations
  const removeJobMutation = useMutation({
    mutationFn: ({ jobId, isDlq }: { jobId: string; isDlq: boolean }) =>
      EdgeMailAdminAPI.removeJob(jobId, queueType, isDlq),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queueJobs'] });
      setSelectedJobs(new Set());
      setSelectedJobDetail(null);
    },
  });

  const retryJobMutation = useMutation({
    mutationFn: (jobId: string) => EdgeMailAdminAPI.retryJob(jobId, queueType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queueJobs'] });
      setSelectedJobDetail(null);
    },
  });

  const replayJobMutation = useMutation({
    mutationFn: (jobId: string) => EdgeMailAdminAPI.replayJob(jobId, queueType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queueJobs'] });
      setSelectedJobDetail(null);
    },
  });

  const bulkRemoveMutation = useMutation({
    mutationFn: (jobIds: string[]) => EdgeMailAdminAPI.bulkRemoveJobs(jobIds, queueType, status === 'dlq'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queueJobs'] });
      setSelectedJobs(new Set());
    },
  });

  const bulkReplayMutation = useMutation({
    mutationFn: (jobIds: string[]) => EdgeMailAdminAPI.bulkReplayJobs(jobIds, queueType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['queueJobs'] });
      setSelectedJobs(new Set());
    },
  });

  // Handlers
  const handleSort = useCallback((field: SortField) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  }, [sortBy]);

  const handleSelectJob = useCallback((jobId: string, checked: boolean) => {
    setSelectedJobs(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(jobId);
      } else {
        next.delete(jobId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked && data?.jobs) {
      setSelectedJobs(new Set(data.jobs.map(j => j.id)));
    } else {
      setSelectedJobs(new Set());
    }
  }, [data?.jobs]);

  const handleExport = useCallback(() => {
    const url = EdgeMailAdminAPI.getExportUrl(queryOptions);
    window.open(url, '_blank');
  }, [queryOptions]);

  const handleStatusChange = useCallback((newStatus: QueueJobStatus) => {
    setStatus(newStatus);
    setPage(1);
    setSelectedJobs(new Set());
    setSelectedJobDetail(null);
  }, []);

  const handleQueueTypeChange = useCallback((newType: QueueType) => {
    setQueueType(newType);
    setPage(1);
    setSelectedJobs(new Set());
    setSelectedJobDetail(null);
  }, []);

  const handleFilterChange = useCallback((key: keyof typeof filters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ domain: '', from: '', to: '', subject: '' });
    setPage(1);
  }, []);

  // Don't render if not open
  if (!isOpen) return null;

  const jobs = data?.jobs || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 0;
  const allSelected = jobs.length > 0 && selectedJobs.size === jobs.length;
  const someSelected = selectedJobs.size > 0;
  const hasActiveFilters = Object.values(filters).some(v => v);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="absolute inset-4 md:inset-8 lg:inset-12 bg-white dark:bg-gray-900 rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-lex-accent-soft rounded-lg">
              <ServerIcon className="h-6 w-6 text-lex-accent" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Queue Job Viewer</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {total} {status} job{total !== 1 ? 's' : ''} in {queueType} queue
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Queue Type Toggle */}
            <div className="flex rounded-lg bg-gray-200 dark:bg-gray-700 p-1">
              <button
                onClick={() => handleQueueTypeChange('inbound')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  queueType === 'inbound'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Inbound
              </button>
              <button
                onClick={() => handleQueueTypeChange('outbound')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  queueType === 'outbound'
                    ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Outbound
              </button>
            </div>
            <button
              onClick={() => refetch()}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Refresh"
            >
              <ArrowPathIcon className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Status Tabs */}
        <div className="flex items-center gap-1 px-4 py-2 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => handleStatusChange(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                status === tab.key
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm border border-gray-200 dark:border-gray-600'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <tab.icon className={`h-4 w-4 ${tab.color}`} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            {/* Bulk Actions */}
            {someSelected && (
              <>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedJobs.size} selected
                </span>
                {status === 'dlq' && (
                  <button
                    onClick={() => bulkReplayMutation.mutate(Array.from(selectedJobs))}
                    disabled={bulkReplayMutation.isPending}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    Replay Selected
                  </button>
                )}
                <button
                  onClick={() => bulkRemoveMutation.mutate(Array.from(selectedJobs))}
                  disabled={bulkRemoveMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  <TrashIcon className="h-4 w-4" />
                  Remove Selected
                </button>
                <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-2" />
              </>
            )}

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(prev => !prev)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-lex-accent-soft text-lex-accent'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <FunnelIcon className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-lex-accent text-lex-accent-contrast rounded-full">
                  {Object.values(filters).filter(v => v).length}
                </span>
              )}
            </button>
          </div>

          <div className="flex items-center gap-2">
            {/* Export */}
            <button
              onClick={handleExport}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text"
                placeholder="Filter by domain..."
                value={filters.domain}
                onChange={e => handleFilterChange('domain', e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Filter by from..."
                value={filters.from}
                onChange={e => handleFilterChange('from', e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Filter by to..."
                value={filters.to}
                onChange={e => handleFilterChange('to', e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-transparent"
              />
              <input
                type="text"
                placeholder="Filter by subject..."
                value={filters.subject}
                onChange={e => handleFilterChange('subject', e.target.value)}
                className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-lex-accent focus:border-transparent"
              />
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                  Clear All
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Jobs Table */}
          <div className={`flex-1 overflow-auto ${selectedJobDetail ? 'border-r border-gray-200 dark:border-gray-700' : ''}`}>
            {isError ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Failed to load queue jobs</p>
                  <button
                    onClick={() => refetch()}
                    className="mt-4 px-4 py-2 text-sm font-medium text-lex-accent-contrast bg-lex-accent hover:bg-lex-accent-hover rounded-lg"
                  >
                    Retry
                  </button>
                </div>
              </div>
            ) : jobs.length === 0 && !isLoading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <EnvelopeIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">No {status} jobs in {queueType} queue</p>
                </div>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0 z-10">
                  <tr>
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={e => handleSelectAll(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-lex-accent focus:ring-lex-accent"
                      />
                    </th>
                    <SortableHeader field="fromAddress" label="From" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader field="toAddress" label="To" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader field="subject" label="Subject" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                    <SortableHeader field="sizeBytes" label="Size" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} className="w-24" />
                    <SortableHeader field="attemptsMade" label="Attempts" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} className="w-24" />
                    <SortableHeader field="queuedAt" label="Queued" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} className="w-36" />
                    <th className="w-32 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Source Client</th>
                    <th className="w-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {isLoading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        <td className="px-4 py-3"><div className="h-4 w-4 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-20 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                        <td className="px-4 py-3"><div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" /></td>
                      </tr>
                    ))
                  ) : (
                    jobs.map(job => (
                      <tr
                        key={job.id}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer transition-colors ${
                          selectedJobDetail?.id === job.id ? 'bg-lex-accent-soft' : ''
                        }`}
                        onClick={() => setSelectedJobDetail(job)}
                      >
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedJobs.has(job.id)}
                            onChange={e => handleSelectJob(job.id, e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-lex-accent focus:ring-lex-accent"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white truncate max-w-[200px]" title={job.fromAddress}>
                          {job.fromAddress}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white truncate max-w-[200px]" title={job.toAddress}>
                          {job.toAddress}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white truncate max-w-[300px]" title={job.subject}>
                          {job.subject || '(no subject)'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {formatBytes(job.sizeBytes)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`${job.attemptsMade >= job.maxAttempts ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-400'}`}>
                            {job.attemptsMade}/{job.maxAttempts}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(job.queuedAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 truncate max-w-[120px]" title={job.sourceClient || ''}>
                          {job.sourceClient || '-'}
                        </td>
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            {(status === 'failed' || status === 'dlq') && (
                              <button
                                onClick={() => status === 'dlq' ? replayJobMutation.mutate(job.id) : retryJobMutation.mutate(job.id)}
                                disabled={retryJobMutation.isPending || replayJobMutation.isPending}
                                className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                                title={status === 'dlq' ? 'Replay' : 'Retry'}
                              >
                                <ArrowPathIcon className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              onClick={() => removeJobMutation.mutate({ jobId: job.id, isDlq: status === 'dlq' })}
                              disabled={removeJobMutation.isPending}
                              className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                              title="Remove"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          {/* Job Detail Panel */}
          {selectedJobDetail && (
            <div className="w-96 flex-shrink-0 overflow-auto bg-gray-50 dark:bg-gray-800/50">
              <JobDetailPanel
                job={selectedJobDetail}
                onClose={() => setSelectedJobDetail(null)}
                onRetry={() => retryJobMutation.mutate(selectedJobDetail.id)}
                onReplay={() => replayJobMutation.mutate(selectedJobDetail.id)}
                onRemove={() => removeJobMutation.mutate({ jobId: selectedJobDetail.id, isDlq: status === 'dlq' })}
                isDlq={status === 'dlq'}
                isRetrying={retryJobMutation.isPending}
                isReplaying={replayJobMutation.isPending}
                isRemoving={removeJobMutation.isPending}
              />
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, total)} of {total} jobs
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface SortableHeaderProps {
  field: SortField;
  label: string;
  sortBy: SortField;
  sortOrder: 'asc' | 'desc';
  onSort: (field: SortField) => void;
  className?: string;
}

const SortableHeader: React.FC<SortableHeaderProps> = ({ field, label, sortBy, sortOrder, onSort, className = '' }) => (
  <th
    className={`px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${className}`}
    onClick={() => onSort(field)}
  >
    <div className="flex items-center gap-1">
      {label}
      {sortBy === field && (
        sortOrder === 'asc' ? <ChevronUpIcon className="h-3 w-3" /> : <ChevronDownIcon className="h-3 w-3" />
      )}
    </div>
  </th>
);

interface JobDetailPanelProps {
  job: QueueJobDetails;
  onClose: () => void;
  onRetry: () => void;
  onReplay: () => void;
  onRemove: () => void;
  isDlq: boolean;
  isRetrying: boolean;
  isReplaying: boolean;
  isRemoving: boolean;
}

const JobDetailPanel: React.FC<JobDetailPanelProps> = ({
  job,
  onClose,
  onRetry,
  onReplay,
  onRemove,
  isDlq,
  isRetrying,
  isReplaying,
  isRemoving,
}) => {
  const statusBadge = getStatusBadge(job.status);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <InformationCircleIcon className="h-5 w-5 text-lex-accent" />
          <span className="font-medium text-gray-900 dark:text-white">Job Details</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Status Badge */}
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text}`}>
            {statusBadge.label}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            ID: {job.id}
          </span>
        </div>

        {/* Email Info */}
        <div className="space-y-3">
          <DetailRow label="From" value={job.fromAddress} copyable />
          <DetailRow label="To" value={job.toAddress} copyable />
          <DetailRow label="Subject" value={job.subject || '(no subject)'} />
          <DetailRow label="Message-ID" value={job.messageId} copyable mono />
          <DetailRow label="Domain" value={job.domain} />
          {job.sourceClient && <DetailRow label="Source Client" value={job.sourceClient} />}
          <DetailRow label="Size" value={formatBytes(job.sizeBytes)} />
        </div>

        {/* Timing */}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-3">
          <DetailRow label="Queued At" value={formatFullDate(job.queuedAt)} />
          {job.processedAt && <DetailRow label="Processed At" value={formatFullDate(job.processedAt)} />}
          {job.finishedAt && <DetailRow label="Finished At" value={formatFullDate(job.finishedAt)} />}
          {job.nextRetryAt && <DetailRow label="Next Retry" value={formatFullDate(job.nextRetryAt)} />}
        </div>

        {/* Retry Info */}
        <div className="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-3">
          <DetailRow
            label="Attempts"
            value={`${job.attemptsMade} / ${job.maxAttempts}`}
            highlight={job.attemptsMade >= job.maxAttempts}
          />
          <DetailRow label="Priority" value={String(job.priority)} />
          {job.delay > 0 && <DetailRow label="Delay" value={`${job.delay}ms`} />}
        </div>

        {/* Error Info */}
        {job.lastError && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-3">
            <div>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Last Error</span>
              {job.errorCode && (
                <span className="ml-2 px-1.5 py-0.5 text-xs font-mono bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded">
                  {job.errorCode}
                </span>
              )}
              <p className="mt-1 text-sm text-red-600 dark:text-red-400 break-words">
                {job.lastError}
              </p>
            </div>
            {job.stackTrace && (
              <div>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Stack Trace</span>
                <pre className="mt-1 text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 rounded p-2 overflow-auto max-h-32">
                  {job.stackTrace}
                </pre>
              </div>
            )}
          </div>
        )}

        {/* Tenant Info */}
        {job.realmId && (
          <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
            <DetailRow label="Realm" value={job.realmId} />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
        {(job.status === 'failed' || isDlq) && (
          <button
            onClick={isDlq ? onReplay : onRetry}
            disabled={isRetrying || isReplaying}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isRetrying || isReplaying ? 'animate-spin' : ''}`} />
            {isDlq ? 'Replay Job' : 'Retry Job'}
          </button>
        )}
        <button
          onClick={onRemove}
          disabled={isRemoving}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
        >
          <TrashIcon className="h-4 w-4" />
          Remove Job
        </button>
      </div>
    </div>
  );
};

interface DetailRowProps {
  label: string;
  value: string;
  copyable?: boolean;
  mono?: boolean;
  highlight?: boolean;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, copyable, mono, highlight }) => {
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
  };

  return (
    <div>
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{label}</span>
      <div className="flex items-center gap-1 mt-0.5">
        <span className={`text-sm break-words ${
          highlight ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-900 dark:text-white'
        } ${mono ? 'font-mono text-xs' : ''}`}>
          {value}
        </span>
        {copyable && (
          <button
            onClick={handleCopy}
            className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded"
            title="Copy to clipboard"
          >
            <DocumentDuplicateIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default QueueJobViewerModal;
